<template>
  <div class="text-input clearfix" v-show="show">
    <div class="form-group row" :class="{ 'has-feedback' : isDirty }">
      <label
        :for="options.name"
        :class="options.fullWidth ? 'col-sm-12' : 'col-sm-3 col-form-label'">
        {{ options.label }}
        <span v-if="options.required" class="required">*</span>
      </label>
      <div :class="options.fullWidth ? 'col-sm-12' : 'col-sm-9'">
        <div :class="{ 'input-group': maxLength }">
          <input class="form-control" type="text"
            :placeholder="options.placeholder || ''"
            :class="formControlValidationClass"
            :value="value"
            @input="validate($event.target.value)"
            :name="options.name"/>
          <div v-if="maxLength && value" class="input-group-append">
            <span class="input-group-text">
              {{ maxLength - value.length >= 0 ? maxLength - value.length : 0 }} chars
            </span>
          </div>
        </div>
        <div :class="validationClass" v-show="validationMessage">
          {{ validationMessage }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import Api from '@/vuex/api'
  import {
    validationClass,
    formControlValidationClass,
    validationMessage
  } from '@/util/helpers'

  export default {
    name: 'TextInput',
    props: {
      value: {
        type: String
      },
      options: {
        type: Object,
        default () {
          return {
            value: ''
          }
        }
      }
    },
    computed: {
      validationClass,
      formControlValidationClass,
      validationMessage,
      invalidMessage () {
        if (this.maxLength && this.value && this.value.length > this.maxLength) {
          return 'Max characters cannot exceed ' + this.maxLength
        } else {
          return this.options.label + ' is required'
        }
      }
    },
    data () {
      return {
        isDirty: false,
        isValid: false,
        isLength: true,
        isRegex: true,
        minLength: 1,
        maxLength: false,
        watcher: false,
        show: true,
        skipFirstWatcherChange: false
      }
    },
    mounted () {
      if (this.options.hidden) this.show = false
      if (this.options.maxLength) { this.maxLength = this.options.maxLength }
      if (this.value !== null || this.value !== '') this.skipFirstWatcherChange = true
      this.validate(this.value)
    },
    methods: {
      validate (val = this.value) {
        val ? this.$emit('input', val.trim()) : this.$emit('input', val)
        if (val) {
          this.isDirty = true
          this.isLength = this.isValid = val.length >= this.minLength
          if (this.isLength && this.maxLength) {
            this.isValid = val.length <= this.maxLength
          }
        } else if (this.options.required) {
          this.isValid = false
          this.isDirty = true
        } else {
          this.isValid = true
        }
      },
      async updateUserValue (val) {
        let instance = new Api()
        let user = await instance.getUser(val)
        if (user) {
          if (this.options.name.toLowerCase().includes('email')) {
            this.validate(user.email)
          } else if (this.options.name.toLowerCase().includes('business')) {
            this.validate(user.businessPhone)
          } else if (this.options.name.toLowerCase().includes('phone')) {
            let phone = user.phone || user.businessPhone || ''
            this.validate(phone)
          } else if (this.options.name.toLowerCase().includes('position')) {
            this.validate(user.positionTitle)
          }
        } else {
          this.validate()
        }
      },
      async updateOcAddressEdit(val) {
        if (this.options.name.toLowerCase().includes('ocaddressname')) {
          this.show = val;
          this.validate(val);
          return
        } else {
          this.validate()
        } 
      },
      async updatePropertyValue (val) {
        let instance = new Api()
        let property = await instance.getProperty(val)
        if (property) {
          if (this.options.name.toLowerCase().includes('name')) {
            this.validate(property.buildingName)
          } else if (this.options.name.toLowerCase().includes('strata')) {
            if (property.strataPlanNumber) this.validate(property.strataPlanNumber)
            else this.validate(property.planOfSubdivisionNumber)
          } else if (this.options.name.toLowerCase().includes('insurance')) {
            this.validate(property.insuranceCompany)
          } else if (this.options.name.toLowerCase().includes('subdivision')) {
            if (property.planOfSubdivisionNumber) this.validate(property.planOfSubdivisionNumber)
            else this.validate(property.strataPlanNumber)
          }
        } else {
          if (this.options.name.toLowerCase().includes('propertyaddressname')) {
            if (!this.skipFirstWatcherChange) {
              this.show = val
              this.validate(val)
            } else {
              this.skipFirstWatcherChange = false
            }
          } else {
            this.validate()
          }
        }
      },
      async updateCompanyValue (val) {
        let instance = new Api()
        let company = await instance.getCompany(val)
        if (company) {
          if (this.options.name === 'ocAccountPhone') {
            this.validate(company.accountPhone)
          } else if (this.options.name === 'ocAccountEmail') {
            this.validate(company.accountEmail)
          } else {
            this.validate(company.postcode)
          }
        } else {
          this.validate()
        }
      },
      async geocode (val) {
        let instance = new Api()
        let addressComponents = await instance.geocode(val)
        if (this.options.name.toLowerCase().includes('suburb')) {
          this.validate(addressComponents.suburb)
        } else if (this.options.name.toLowerCase().includes('postcode')) {
          this.validate(addressComponents.postcode)
        }
      },
      async updateContractorValue (val) {
        let instance = new Api()
        let contractor = await instance.getContractor(val)
        if (contractor) {
          if (this.options.name.toLowerCase().includes('email')) {
            this.validate(contractor.email)
          } else if (this.options.name.toLowerCase().includes('phone')) {
            this.validate(contractor.phoneNumber)
          } else if (this.options.name.toLowerCase().includes('mobile')) {
            this.validate(contractor.mobileNumber)
          } else if (this.options.name.toLowerCase().includes('fax')) {
            this.validate(contractor.faxNumber)
          } else if (this.options.name.toLowerCase().includes('address')) {
            this.validate(contractor.address)
          } else if (this.options.name.toLowerCase().includes('addressEdit')) {
            this.validate(contractor.addressEdit)
          } 
          else if (this.options.name.toLowerCase().includes('company')) {
            this.validate(contractor.company)
          } else if (this.options.name.toLowerCase().includes('name')) {
            this.validate(contractor.contactPersonName)
          } else if (this.options.name.toLowerCase().includes('notes')) {
            this.validate(contractor.notes)
          }
        } else {
          this.validate()
        }
      },
      async checkMultipleAccounts (val) {
        if (val) {
          let instance = new Api()
          let response = await instance.hasMultipleAccounts(val)
          if (response.multipleAccounts) {
            this.show = true
            this.options.required = true
            this.validate('')
          } else {
            this.show = false
            this.options.required = false
            this.validate()
          }
        }
      },
      async getReportName (val) {
        let instance = new Api()
        let name = await instance.getReportName(val)
        this.validate(name)
      },
      runWatcher (val, name) {
        if (this.options.watchers.includes(name)) {
          if (!this.value || name === 'isCustom') this.watcher = true
          if (this.watcher) {
            if (val || val === false) {
              switch (name) {
                case 'StaffMemberUuid':
                  this.updateUserValue(val)
                  break
                case 'email':
                  this.checkMultipleAccounts(val)
                  break
                case 'ocManagerName':
                  this.updateUserValue(val)
                  break
                case 'companyName':
                  this.updateCompanyValue(val)
                  break
                case 'ocAddress':
                  this.updateOcAddressEdit(val)
                  break;
                case 'propertyAddress':
                  this.updatePropertyValue(val)
                  break
                case 'address':
                  this.geocode(val)
                  break
                case 'contractor':
                  this.updateContractorValue(val)
                  break
                case 'isCustom':
                  if (this.options.name === 'customReport') {
                    this.show = val
                    this.$emit('input', '')
                  } else if (this.options.name === 'reportName') {
                    this.show = !val
                    this.$emit('input', '')
                  }
                  break
                case 'reportType':
                  if (!this.skipFirstWatcherChange) {
                    this.show = val
                    this.getReportName(val)
                  } else {
                    this.skipFirstWatcherChange = false
                  }
                  break
                case 'hasKeySafe':
                  if (!val) this.validate('')
                  this.show = val
                  this.options.required = val
                  break
                case 'hasESMserviceContract':
                  if (!val) this.validate('')
                  this.show = val
                  this.options.required = val
                  break
                case 'hasEmbeddedServices':
                  if (!val) this.validate('')
                  this.show = val
                  this.options.required = val
                  break
                case 'hasElectricityContract':
                  if (!val) this.validate('')
                  this.show = val
                  this.options.required = val
                  break
                case 'hasHotWaterContract':
                  if (!val) this.validate('')
                  this.show = val
                  this.options.required = val
                  break
                case 'hasLiftContract':
                  if (!val) this.validate('')
                  this.show = val
                  this.options.required = val
                  break
                case 'hasGarageDoorContract':
                  if (!val) this.validate('')
                  this.show = val
                  this.options.required = val
                  break
                case 'hasAccessControlContract':
                  if (!val) this.validate('')
                  this.show = val
                  this.options.required = val
                  break
                case 'hasAccessDoorContract':
                  if (!val) this.validate('')
                  this.show = val
                  this.options.required = val
                  break
                case 'hasVentilationContract':
                  if (!val) this.validate('')
                  this.show = val
                  this.options.required = val
                  break
                case 'hasCleaningContract':
                  if (!val) this.validate('')
                  this.show = val
                  this.options.required = val
                  break
                case 'hasFacilityManager':
                  if (!val) this.validate('')
                  this.show = val
                  this.options.required = val
                  break
                case 'hasAltAddress':
                  if (!val) this.validate('')
                  this.show = val
                  this.options.required = val
                  break
              }
            } else {
              this.validate('')
              this.isDirty = false
            }
          }
          this.watcher = true
        }
      }
    }
  }
</script>



// WEBPACK FOOTER //
// src/components/inputs/TextInput.vue